import { useEffect, useRef, useState } from "react";
import { motion, animate, useReducedMotion } from "framer-motion";

export const ease = [0.2, 0.8, 0.2, 1];
export const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } } };
export const fadeUp = { hidden: { opacity: 0, y: 18, filter: "blur(4px)" }, show: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.55, ease } } };

export const CountUp = ({ value, format = (n) => new Intl.NumberFormat().format(Math.round(n)), className, testId }) => {
  const reduce = useReducedMotion();
  const [display, setDisplay] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    if (typeof value !== "number") return;
    if (reduce) { setDisplay(value); prev.current = value; return; }
    const ctrl = animate(prev.current, value, { duration: 1.1, ease, onUpdate: (v) => setDisplay(v) });
    prev.current = value;
    return () => ctrl.stop();
  }, [value, reduce]);
  if (typeof value !== "number") return <span className={className} data-testid={testId}>{value ?? "—"}</span>;
  return <span className={`tabular-nums ${className || ""}`} data-testid={testId}>{format(display)}</span>;
};

export const Spotlight = ({ children, className = "", ...rest }) => {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - r.left}px`);
    el.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  return (
    <motion.div ref={ref} onMouseMove={onMove} variants={fadeUp} whileHover={{ y: -4 }} transition={{ type: "spring", stiffness: 300, damping: 24 }}
      className={`spotlight panel ${className}`} {...rest}>
      {children}
    </motion.div>
  );
};

export const Sparkline = ({ data = [], color = "hsl(var(--primary))", height = 36, testId }) => {
  if (!data.length) return <div style={{ height }} />;
  const w = 120, max = Math.max(...data, 1), min = Math.min(...data, 0);
  const pts = data.map((v, i) => [(i / Math.max(data.length - 1, 1)) * w, height - ((v - min) / (max - min || 1)) * (height - 4) - 2]);
  const d = pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${height}`} className="w-full overflow-visible" style={{ height }} data-testid={testId} preserveAspectRatio="none">
      <defs><linearGradient id="spk" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity="0.35" /><stop offset="100%" stopColor={color} stopOpacity="0" /></linearGradient></defs>
      <motion.path d={`${d} L${w},${height} L0,${height} Z`} fill="url(#spk)" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6, duration: 0.6 }} />
      <motion.path d={d} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.2, ease }} />
    </svg>
  );
};
